import { useState } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { useToast } from "../../context/ToastContext";
import { useRefreshAll, useShift } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatDateTime, money } from "../../lib/format";
import type { ShiftReport } from "../../lib/types";
import { Pill, SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { Modal } from "../ui/Modal";
import { PrintButton } from "../ui/PrintButton";

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between text-xs ${strong ? "font-black text-cosmic-navy" : "font-bold text-gray-600"}`}>
    <span>{label}</span>
    <span className="num font-mono">{value}</span>
  </div>
);

/** Put cash in / take cash out of the drawer during the shift, with a reason (change, a supplier paid in cash, a drop to the safe). */
function CashMove({ shiftId, onClose, onDone }: { shiftId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const [kind, setKind] = useState<"in" | "out">("out");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const fils = amount.trim() === "" ? NaN : jod(Number(amount));
  async function save() {
    setBusy(true);
    try {
      await api.post(`/shifts/${shiftId}/cash-movement`, { kind, amount: fils, reason: reason.trim() });
      toast(t("zreport.moved"), "success");
      onDone();
    } catch (e) { toastError(e); } finally { setBusy(false); }
  }
  return (
    <Modal open size="sm" onClose={onClose} title={t("zreport.cashMove")}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || Number.isNaN(fils) || fils <= 0 || reason.trim().length < 3} onClick={() => void save()}>{t("zreport.saveMove")}</button></>}>
      <div className="flex gap-2" role="group" aria-label={t("zreport.cashMove")}>
        {(["out", "in"] as const).map((k) => <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)} className={`flex-1 rounded-xl px-3 py-2 text-xs font-black ${kind === k ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{k === "in" ? "➕ " : "➖ "}{t(k === "in" ? "zreport.cashIn" : "zreport.cashOut")}</button>)}
      </div>
      <div><label className="label" htmlFor="cm-amount">{t("zreport.moveAmount", { cur: t("currency") })}</label><input id="cm-amount" className="field num font-mono" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
      <div><label className="label" htmlFor="cm-reason">{t("zreport.moveReason")}</label><input id="cm-reason" className="field" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
    </Modal>
  );
}

/** Cash-drawer reconciliation for the terminal's open shift, with close-and-export. */
export function ZReport() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const { data } = useShift();
  const [counted, setCounted] = useState("");
  const [closed, setClosed] = useState<{ id: string; report: ShiftReport } | null>(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);

  const shift = data?.shift;
  const live = data?.report;
  const r = closed?.report ?? live;
  if (!shift && !closed) {
    return <section className="card p-5 text-xs font-bold text-gray-500">{t("shift.noneOpen")}</section>;
  }
  if (!r) return null;

  const cur = t("currency");
  const countedFils = counted.trim() === "" ? null : jod(Number(counted));
  const variance = closed ? r.variance : countedFils === null || Number.isNaN(countedFils) ? null : countedFils - r.expectedCash;
  const vTone = variance === null ? "gray" : variance === 0 ? "green" : "red";

  async function closeShift() {
    if (!shift || countedFils === null || Number.isNaN(countedFils)) return;
    setBusy(true);
    try {
      const res = await api.post<{ report: ShiftReport }>(`/shifts/${shift.id}/close`, { countedCash: countedFils });
      setClosed({ id: shift.id, report: res.report });
      toast(t("shift.closedOk"), "success");
      refresh();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-3.5 p-4 sm:p-5" aria-label={t("zreport.title")}>
      <SectionHeader icon="💰" title={t("zreport.title")} right={<Pill tone={closed ? "gray" : "green"}>{closed ? t("shift.closed") : `${t("shift.open")} • ${r.terminal}`}</Pill>} />
      <div className="space-y-2">
        <Row label={t("zreport.openedAt")} value={formatDateTime(r.openedAt, locale)} />
        <Row label={t("zreport.openingFloat")} value={`${money(r.openingFloat)} ${cur}`} />
        <Row label={t("zreport.cashSales")} value={`${money(r.cashSales)} ${cur}`} />
        {!!r.cashIn && <Row label={t("zreport.cashIn")} value={`+${money(r.cashIn)} ${cur}`} />}
        {!!r.cashOut && <Row label={t("zreport.cashOut")} value={`-${money(r.cashOut)} ${cur}`} />}
        <Row strong label={t("zreport.expectedCash")} value={`${money(r.expectedCash)} ${cur}`} />
        <Row label={t("zreport.card")} value={`${money(r.cardTotal)} ${cur}`} />
        <Row label={t("zreport.cliq")} value={`${money(r.cliqTotal)} ${cur}`} />
        <Row label={t("zreport.credit")} value={`${money(r.creditSales)} ${cur}`} />
        <Row strong label={t("zreport.gross")} value={`${money(r.grossSales)} ${cur}`} />
        {r.discountTotal > 0 && <Row label={t("zreport.discounts")} value={`-${money(r.discountTotal)} ${cur}`} />}
        <Row label={t("zreport.transactions")} value={`${r.transactionCount}${r.voidCount ? ` (${t("zreport.voids", { count: r.voidCount })})` : ""}`} />
      </div>
      {!!r.movements?.length && <ul className="rounded-xl bg-gray-50 p-2 text-[11px] font-semibold text-gray-600" aria-label={t("zreport.movements")}>{r.movements.map((m) => <li key={m.id}>{m.kind === "in" ? "➕" : "➖"} <span className="num font-mono">{money(m.amount)}</span> · {m.reason}</li>)}</ul>}
      {!closed && shift && (
        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={() => setMoving(true)}>💵 {t("zreport.cashMove")}</button>
          <PrintButton path={`/documents/z-report/${shift.id}?locale=${locale}`} className="btn-ghost flex-1">{t("zreport.xReport")}</PrintButton>
        </div>
      )}
      <div className="flex items-end justify-between gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex-1">
          <label className="label" htmlFor="counted">{t("zreport.counted")}</label>
          <input id="counted" className="field num font-mono" inputMode="decimal" value={closed ? money(r.countedCash ?? 0) : counted} onChange={(e) => setCounted(e.target.value)} disabled={!!closed} placeholder={money(r.expectedCash)} />
        </div>
        <div className="text-end">
          <span className="block text-[10px] font-black uppercase text-gray-500">{t("zreport.variance")}</span>
          <Pill tone={vTone}>
            <span className="num">{variance === null ? "—" : `${variance > 0 ? "+" : ""}${money(variance)}`}</span>
            {variance === 0 ? ` (${t("zreport.balanced")})` : ""}
          </Pill>
        </div>
      </div>
      {closed ? (
        <div className="flex gap-2">
          <DownloadButton path={`/documents/z-report/${closed.id}?locale=${locale}`} fallbackName="Z-Report.pdf" className="btn-primary flex-1 !py-3">📄 {t("zreport.downloadPdf")}</DownloadButton>
          <PrintButton path={`/documents/z-report/${closed.id}?locale=${locale}`} className="btn-dark flex-1 !py-3" />
        </div>
      ) : (
        <button type="button" className="btn-dark w-full !py-3" disabled={busy || countedFils === null || Number.isNaN(countedFils)} onClick={closeShift}>🔒 {t("zreport.closeShift")}</button>
      )}
    {moving && shift && <CashMove shiftId={shift.id} onClose={() => setMoving(false)} onDone={() => { setMoving(false); refresh(); }} />}
    </section>
  );
}
